import { api } from "./api";

export interface EnrollmentStudent {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
    createdAt: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  progress: number;
  status: string;
  enrolledAt: string;
  completedAt?: string;
  lastAccessedAt?: string;
}

export interface EnrollmentCourse {
  _id: string;
  title: string;
  thumbnail: string;
  shortDescription?: string;
  description?: string;
  difficulty?: string;
  rating?: number;
  price?: number;
  discount?: number;
  finalPrice?: number;
  currency?: string;
  instructorName?: string;
  instructorAvatar?: string;
}

export interface Enrollment {
  _id: string;
  user: string;
  course: EnrollmentCourse;
  status: "active" | "completed" | "paused" | "cancelled";
  progress: number;
  completedLessons: string[];
  completedModules: string[];
  completedQuizzes: string[];
  lastAccessedLesson?: string;
  enrolledAt: string;
  completedAt?: string;
  expiresAt?: string;
}

export const enrollmentApi = {
  // Enroll in a course
  enrollInCourse: (
    courseId: string,
    data?: { paymentId?: string; paymentMethod?: string },
  ) => {
    return api.post<{ success: boolean; message: string; data: Enrollment }>(
      `/enrollments/${courseId}/enroll`,
      data || {},
    );
  },

  // Complete enrollment after payment
  completeEnrollment: (
    courseId: string,
    data: { paymentId?: string; paymentMethod: string; transactionId?: string },
  ) => {
    return api.post<{ success: boolean; message: string; data: Enrollment }>(
      `/enrollments/${courseId}/enroll/payment-success`,
      data,
    );
  },

  // Alias for completeEnrollment - use this in PaymentSuccessPage
  enrollAfterPayment: (
    courseId: string,
    data: { paymentId?: string; paymentMethod: string; transactionId?: string },
  ) => {
    return enrollmentApi.completeEnrollment(courseId, data);
  },

  // Get user's all enrollments
  getUserEnrollments: (options?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.status) params.append("status", options.status);

    return api.get<{
      success: boolean;
      count: number;
      data: Enrollment[];
      pagination: any;
    }>(
      `/enrollments/users/enrollments${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  // Get enrollment for a specific course
  checkEnrollmentStatus: async (courseId: string) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: Enrollment | null;
      }>(`/enrollments/${courseId}/enrollment`);
      return {
        isEnrolled: response.success && !!response.data,
        enrollment: response.data,
      };
    } catch (error) {
      // 404 or any error means not enrolled
      return { isEnrolled: false, enrollment: null };
    }
  },

  // Get enrollment for a specific course (alias)
  getCourseEnrollment: (courseId: string) => {
    return api.get<{
      success: boolean;
      data: Enrollment;
    }>(`/enrollments/${courseId}/enrollment`);
  },

  // Mark lesson as complete
  completeLesson: (courseId: string, lessonId: string) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/enrollments/${courseId}/lessons/${lessonId}/complete`,
      {},
    );
  },

  // Get last accessed lesson
  getLastAccessed: (courseId: string) => {
    return api.get<{
      success: boolean;
      data: { lessonId: string; accessedAt: string };
    }>(`/enrollments/${courseId}/enrollment/last-accessed`);
  },

  // Update last accessed lesson
  updateLastAccessed: (courseId: string, lessonId: string) => {
    return api.put<{ success: boolean; message: string }>(
      `/enrollments/${courseId}/enrollment/last-accessed`,
      { lessonId },
    );
  },

  // Get course progress
  getCourseProgress: (courseId: string) => {
    return api.get<{
      success: boolean;
      data: {
        progress: number;
        completedLessons: number;
        totalLessons: number;
      };
    }>(`/enrollments/${courseId}/progress`);
  },

  // Submit quiz score
  submitQuizScore: (
    courseId: string,
    quizId: string,
    data: { score: number; answers: Record<string, string> },
  ) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/enrollments/${courseId}/quizzes/${quizId}/score`,
      data,
    );
  },

  // Get all quiz scores for a course
  getQuizScores: (courseId: string) => {
    return api.get<{ success: boolean; data: any[] }>(
      `/enrollments/${courseId}/quiz-scores`,
    );
  },

  // Get quiz score for a specific quiz
  getQuizScore: (courseId: string, quizId: string) => {
    return api.get<{ success: boolean; data: any }>(
      `/enrollments/${courseId}/quizzes/${quizId}/score`,
    );
  },

  // Update enrollment status (pause/resume)
  updateEnrollmentStatus: (courseId: string, status: "active" | "paused") => {
    return api.put<{ success: boolean; message: string }>(
      `/enrollments/${courseId}/enrollment/status`,
      { status },
    );
  },

  // Drop/unenroll from course
  dropCourse: (courseId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/enrollments/${courseId}/enroll`,
    );
  },

  // Get all enrollments for a course (Instructor/Admin)
  getCourseEnrollments: (
    courseId: string,
    options?: { page?: number; limit?: number },
  ) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    return api.get<{
      success: boolean;
      count: number;
      data: any[];
      pagination: any;
    }>(
      `/enrollments/${courseId}/enrollments${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  // Get enrolled courses for current user (via enrollments)
  getEnrolledCourses: (options?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.status) params.append("status", options.status);

    return api.get<{
      success: boolean;
      count: number;
      data: any[];
      pagination: any;
    }>(
      `/enrollments/users/enrollments${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  // Get instructor analytics
  getInstructorAnalytics: () => {
    return api.get<{
      success: boolean;
      data: {
        summary: {
          totalCourses: number;
          totalEnrollments: number;
          activeEnrollments: number;
          completedEnrollments: number;
          totalRevenue: number;
          averageEnrollmentsPerCourse: number;
        };
        enrollmentsOverTime: { _id: string; count: number }[];
        courseStats: {
          courseId: string;
          courseTitle: string;
          totalEnrollments: number;
          completedEnrollments: number;
          completionRate: number;
          revenue: number;
        }[];
      };
    }>("/enrollments/instructor/enrollment-analytics");
  },

  // Get instructor's students
  getInstructorStudents: (options?: {
    page?: number;
    limit?: number;
    search?: string;
    courseId?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.search) params.append("search", options.search);
    if (options?.courseId) params.append("courseId", options.courseId);

    return api.get<{
      success: boolean;
      count: number;
      data: EnrollmentStudent[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(
      `/enrollments/instructor/students${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  // Get certificate exam status
  getCertificateExamStatus: (courseId: string) => {
    return api.get<{
      success: boolean;
      data: {
        hasCertificateExam: boolean;
        exam?: any; // Quiz with populated questions
        examPassed: boolean;
        examScore?: number;
        examAttempts: number;
        lastAttempt?: { score: number; passed: boolean; completedAt: string };
      };
    }>(`/enrollments/${courseId}/certificate-exam`);
  },

  // Submit certificate exam
  submitCertificateExam: (
    courseId: string,
    data: { score: number; answers: Record<string, string>; attempt: number },
  ) => {
    return api.post<{
      success: boolean;
      message: string;
      data: {
        quiz: any;
        score: number;
        passed: boolean;
        attempts: number;
        courseCompleted: boolean;
        certificateIssued: boolean;
        certificateUrl?: string;
      };
    }>(`/enrollments/${courseId}/certificate-exam/submit`, data);
  },
};
